import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiHome, FiFileText, FiCreditCard, FiUsers, FiBook,
  FiMessageSquare, FiLogOut, FiMenu, FiX, FiCheck,
  FiXCircle, FiEdit2, FiTrash2, FiPlus, FiRefreshCw, FiMonitor, FiVideo
} from 'react-icons/fi';
import AdminLmsPage from './AdminLmsPage';
import AdminLiveClasses from './AdminLiveClasses';
import {
  getAdminStats, getAdminApplications, updateApplicationStatus,
  getAdminPayments, updatePaymentStatus, getAdminUsers,
  getAdminCourses, createCourse, updateCourse, deleteCourse,
  getAdminTestimonials, createTestimonial,
  updateTestimonial, deleteTestimonial
} from '../utils/api';
import { useAuth } from '../context/AuthContext';

/* ─── Sidebar ─── */
const Sidebar = ({ open, onClose }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const links = [
    { to: '/admin', icon: <FiHome />, label: 'Dashboard', end: true },
    { to: '/admin/applications', icon: <FiFileText />, label: 'Applications' },
    { to: '/admin/payments', icon: <FiCreditCard />, label: 'Payments' },
    { to: '/admin/users', icon: <FiUsers />, label: 'Students' },
    { to: '/admin/courses', icon: <FiBook />, label: 'Courses' },
    { to: '/admin/testimonials', icon: <FiMessageSquare />, label: 'Testimonials' },
    { to: '/admin/lms', icon: <FiMonitor />, label: 'LMS Content' },
    { to: '/admin/live', icon: <FiVideo />, label: 'Live Classes' },
  ];
  return (
    <>
      <style>{`
        .admin-sidebar { width: 240px; background: var(--dark); min-height: 100vh; display: flex; flex-direction: column; position: fixed; top: 0; left: 0; z-index: 200; transition: transform 0.3s ease; }
        .admin-sidebar.closed { transform: translateX(-100%); }
        .admin-logo { padding: 24px 20px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); }
        .admin-logo-icon { width: 36px; height: 36px; background: linear-gradient(135deg, var(--primary), var(--accent)); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 18px; }
        .admin-logo span { color: white; font-family: var(--font-display); font-weight: 700; font-size: 18px; }
        .admin-logo small { color: #64748b; font-size: 10px; display: block; }
        .admin-nav { padding: 16px 12px; flex: 1; }
        .admin-nav-link { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-radius: var(--radius); color: #94a3b8; font-size: 14px; font-weight: 500; margin-bottom: 4px; transition: var(--transition); }
        .admin-nav-link:hover, .admin-nav-link.active { background: rgba(26,86,219,0.2); color: white; }
        .admin-nav-link.active { background: var(--primary); color: white; }
        .admin-sidebar-footer { padding: 16px 12px; border-top: 1px solid rgba(255,255,255,0.08); }
        .admin-logout { display: flex; align-items: center; gap: 10px; color: #ef4444; font-size: 14px; font-weight: 500; cursor: pointer; background: none; border: none; padding: 10px 14px; border-radius: var(--radius); width: 100%; transition: var(--transition); }
        .admin-logout:hover { background: rgba(239,68,68,0.15); }
        @media (max-width: 900px) {
          .admin-sidebar { transform: translateX(-100%); }
          .admin-sidebar.open { transform: translateX(0); box-shadow: var(--shadow-xl); }
        }
      `}</style>
      <div className={`admin-sidebar${open ? ' open' : ''}`}>
        <div className="admin-logo">
          <div className="admin-logo-icon">K</div>
          <div><span>Korpnex</span><small>Admin Panel</small></div>
        </div>
        <nav className="admin-nav">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `admin-nav-link${isActive ? ' active' : ''}`} onClick={onClose}>
              {l.icon} {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-link" style={{ marginBottom: 8 }}>← View Website</Link>
          <button className="admin-logout" onClick={() => { logout(); navigate('/'); }}>
            <FiLogOut /> Logout
          </button>
        </div>
      </div>
    </>
  );
};

/* ─── Top Bar ─── */
const TopBar = ({ title, onMenuClick }) => (
  <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button onClick={onMenuClick} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none', fontSize: 20 }} className="admin-menu-btn"><FiMenu /></button>
      <h2 style={{ fontSize: 18, fontWeight: 700 }}>{title}</h2>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '6px 14px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600 }}>Admin</div>
    </div>
    <style>{`.admin-menu-btn { display: none !important; } @media (max-width: 900px) { .admin-menu-btn { display: flex !important; } }`}</style>
  </div>
);

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const map = {
    pending: ['badge-warning', 'Pending'], under_review: ['badge-primary', 'Under Review'],
    approved: ['badge-success', 'Approved'], rejected: ['badge-error', 'Rejected'],
    enrolled: ['badge-success', 'Enrolled'], paid: ['badge-success', 'Paid'],
    created: ['badge-warning', 'Created'], failed: ['badge-error', 'Failed'],
    active: ['badge-success', 'Active'],
  };
  const [cls, label] = map[status] || ['badge-dark', status];
  return <span className={`badge ${cls}`}>{label}</span>;
};

/* ─── Stat Card ─── */
const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--dark)' }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--mid)' }}>{label}</div>
    </div>
  </div>
);

/* ─── Dashboard Overview ─── */
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { getAdminStats().then(r => setStats(r.data.stats)).catch(() => {}); }, []);
  if (!stats) return <div className="spinner-wrap"><div className="spinner" /></div>;
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard icon="📋" label="Total Applications" value={stats.totalApplications} color="#1a56db" />
        <StatCard icon="⏳" label="Pending Review" value={stats.pendingApplications} color="#e8590c" />
        <StatCard icon="💰" label="Total Revenue" value={`₹${stats.totalRevenue?.toLocaleString()}`} color="#059669" />
        <StatCard icon="🎓" label="Total Students" value={stats.totalStudents} color="#7c3aed" />
      </div>
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>Recent Applications</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--bg)', fontSize: 12, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {['Name', 'Course', 'Applied On', 'Status'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(stats.recentApplications || []).map((a, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)', fontSize: 14 }}>
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{a.fullName}</td>
                <td style={{ padding: '12px 16px', color: 'var(--mid)' }}>{a.course?.title}</td>
                <td style={{ padding: '12px 16px', color: 'var(--mid)' }}>{new Date(a.createdAt).toLocaleDateString('en-IN')}</td>
                <td style={{ padding: '12px 16px' }}><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Applications ─── */
const AdminApplications = () => {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getAdminApplications(filter ? { status: filter } : {})
      .then(r => setApps(r.data.applications || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await updateApplicationStatus(id, { status });
      toast.success(`Application ${status}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const statuses = ['', 'pending', 'under_review', 'approved', 'rejected', 'enrolled'];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {statuses.map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`btn btn-sm${filter === s ? ' btn-primary' : ' btn-outline'}`} style={{ textTransform: 'capitalize' }}>
              {s || 'All'}
            </button>
          ))}
        </div>
        <button onClick={load} className="btn btn-outline btn-sm"><FiRefreshCw size={13} /> Refresh</button>
      </div>

      {loading ? <div className="spinner-wrap"><div className="spinner" /></div> : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: 'var(--bg)', fontSize: 12, color: 'var(--mid)', textTransform: 'uppercase' }}>
              {['#', 'Student', 'Email', 'Phone', 'College', 'Course', 'Year', 'Applied', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {apps.map((a, i) => (
                <tr key={a._id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)', fontSize: 12 }}>{a.applicationNumber}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{a.fullName}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)' }}>{a.email}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)' }}>{a.phone}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.collegeName}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.course?.title}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)' }}>{a.yearOfStudy}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--mid)', whiteSpace: 'nowrap' }}>{new Date(a.createdAt).toLocaleDateString('en-IN')}</td>
                  <td style={{ padding: '10px 12px' }}><StatusBadge status={a.status} /></td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {a.status === 'pending' && (
                        <>
                          <button onClick={() => updateStatus(a._id, 'approved')} className="btn btn-sm" style={{ background: '#d1fae5', color: 'var(--success)', padding: '4px 10px' }} title="Approve"><FiCheck size={13} /></button>
                          <button onClick={() => updateStatus(a._id, 'rejected')} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)', padding: '4px 10px' }} title="Reject"><FiXCircle size={13} /></button>
                        </>
                      )}
                      {a.status !== 'enrolled' && (
                        <select value={a.status} onChange={e => updateStatus(a._id, e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--dark)' }}>
                          {['pending', 'under_review', 'approved', 'rejected', 'enrolled'].map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</option>)}
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {apps.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>No applications found.</div>}
        </div>
      )}
    </div>
  );
};

/* ─── Payments ─── */
const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getAdminPayments(filter ? { status: filter } : {})
      .then(r => setPayments(r.data.payments || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try { await updatePaymentStatus(id, { status }); toast.success('Payment updated'); load(); }
    catch { toast.error('Update failed'); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['', 'paid', 'created', 'failed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn btn-sm${filter === s ? ' btn-primary' : ' btn-outline'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>
      {loading ? <div className="spinner-wrap"><div className="spinner" /></div> : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: 'var(--bg)', fontSize: 12, color: 'var(--mid)', textTransform: 'uppercase' }}>
              {['Invoice', 'Student', 'Course', 'Amount', 'Type', 'Status', 'Date', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p._id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--mid)', fontWeight: 600 }}>{p.invoiceNumber || '—'}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 500 }}>{p.userId?.fullName}<div style={{ fontSize: 11, color: 'var(--mid)' }}>{p.userId?.email}</div></td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{p.courseId?.title}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--success)' }}>₹{(p.amount / 100).toLocaleString()}</td>
                  <td style={{ padding: '10px 14px' }}><span className="badge badge-primary" style={{ textTransform: 'capitalize' }}>{p.type}</span></td>
                  <td style={{ padding: '10px 14px' }}><StatusBadge status={p.status} /></td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)', whiteSpace: 'nowrap' }}>{p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <select value={p.status} onChange={e => updateStatus(p._id, e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>
                      {['created', 'paid', 'failed', 'refunded'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>No payments found.</div>}
        </div>
      )}
    </div>
  );
};

/* ─── Students ─── */
const AdminStudents = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getAdminUsers().then(r => setUsers(r.data.users || [])).finally(() => setLoading(false)); }, []);

  return (
    <div style={{ padding: 24 }}>
      {loading ? <div className="spinner-wrap"><div className="spinner" /></div> : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14, color: 'var(--mid)' }}>
            {users.length} students registered
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: 'var(--bg)', fontSize: 12, color: 'var(--mid)', textTransform: 'uppercase' }}>
              {['Name', 'Email', 'Phone', 'College', 'Year', 'Enrolled Courses', 'Joined'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u._id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{u.fullName}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{u.email}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{u.phone || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{u.collegeName || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{u.yearOfStudy || '—'}</td>
                  <td style={{ padding: '10px 14px' }}><span className="badge badge-primary">{u.enrolledCourses?.length || 0}</span></td>
                  <td style={{ padding: '10px 14px', color: 'var(--mid)' }}>{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>No students yet.</div>}
        </div>
      )}
    </div>
  );
};

/* ─── Courses ─── */
/* ─── Full Course Editor ─── */
const BLANK_COURSE = {
  title: '', slug: '', subtitle: '', description: '', duration: '',
  category: 'Development', badge: '', rating: 4.8,
  fee: { total: 15000, registration: 2000, remaining: 13000 },
  isActive: true,
  highlights: [],
  tools: [],
  projects: [],
  curriculum: [],
  internshipDetails: { duration: '1 Month', type: 'Remote/Hybrid', stipend: 'Performance Based', description: '' },
};

/* small helper — editable list of strings */
const StringList = ({ label, hint, values, onChange, placeholder }) => {
  const [draft, setDraft] = React.useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...values, v]);
    setDraft('');
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="form-group">
      <label>{label} {hint && <span style={{ fontSize:11,color:'var(--mid)',fontWeight:400 }}>{hint}</span>}</label>
      <div style={{ display:'flex',gap:8,marginBottom:8 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)}
          className="form-control" placeholder={placeholder}
          onKeyDown={e => e.key==='Enter' && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="btn btn-primary btn-sm" style={{ flexShrink:0 }}>Add</button>
      </div>
      <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
        {values.map((v,i) => (
          <span key={i} style={{ background:'var(--primary-light)',color:'var(--primary)',fontSize:13,padding:'4px 10px',borderRadius:999,display:'flex',alignItems:'center',gap:6 }}>
            {v}
            <button type="button" onClick={() => remove(i)} style={{ background:'none',border:'none',cursor:'pointer',color:'var(--primary)',lineHeight:1,padding:0,fontSize:15 }}>×</button>
          </span>
        ))}
        {values.length === 0 && <span style={{ fontSize:12,color:'var(--mid)' }}>None added yet</span>}
      </div>
    </div>
  );
};

const AdminCourses = () => {
  const [courses,  setCourses]  = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [modal,    setModal]    = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [form,     setForm]     = React.useState(BLANK_COURSE);
  const [saving,   setSaving]   = React.useState(false);
  const [tab,      setTab]      = React.useState('basic'); // basic | content | internship

  const load = () => {
    setLoading(true);
    getAdminCourses().then(r => setCourses(r.data.courses || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => {
    setForm({ ...BLANK_COURSE,
      highlights: [], tools: [], projects: [], curriculum: [],
      internshipDetails: { duration: '1 Month', type: 'Remote/Hybrid', stipend: 'Performance Based', description: '' }
    });
    setEditItem(null); setTab('basic'); setModal(true);
  };

  const openEdit = (c) => {
    setForm({
      title:       c.title       || '',
      slug:        c.slug        || '',
      subtitle:    c.subtitle    || '',
      description: c.description || '',
      duration:    c.duration    || '',
      category:    c.category    || 'Development',
      badge:       c.badge       || '',
      rating:      c.rating      || 4.8,
      fee: { total: c.fee?.total||0, registration: c.fee?.registration||0, remaining: c.fee?.remaining||0 },
      isActive:    c.isActive ?? true,
      highlights:  c.highlights  || [],
      tools:       c.tools       || [],
      projects:    c.projects    || [],
      curriculum:  c.curriculum  || [],
      internshipDetails: {
        duration:    c.internshipDetails?.duration    || '1 Month',
        type:        c.internshipDetails?.type        || 'Remote/Hybrid',
        stipend:     c.internshipDetails?.stipend     || 'Performance Based',
        description: c.internshipDetails?.description || '',
      },
    });
    setEditItem(c); setTab('basic'); setModal(true);
  };

  const setF   = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFee = (k, v) => setForm(f => ({ ...f, fee: { ...f.fee, [k]: Number(v) } }));
  const setInternship = (k, v) => setForm(f => ({ ...f, internshipDetails: { ...f.internshipDetails, [k]: v } }));

  /* curriculum helpers */
  const addCurriculumWeek = () => setForm(f => ({
    ...f, curriculum: [...f.curriculum, { week: 'Week ' + (f.curriculum.length+1), topic: '', subtopics: [] }]
  }));
  const updateCurriculumWeek = (i, k, v) => setForm(f => {
    const c = [...f.curriculum];
    c[i] = { ...c[i], [k]: v };
    return { ...f, curriculum: c };
  });
  const removeCurriculumWeek = (i) => setForm(f => ({ ...f, curriculum: f.curriculum.filter((_,idx)=>idx!==i) }));
  const addSubtopic = (i, val) => {
    if (!val.trim()) return;
    setForm(f => {
      const c = [...f.curriculum];
      c[i] = { ...c[i], subtopics: [...(c[i].subtopics||[]), val.trim()] };
      return { ...f, curriculum: c };
    });
  };
  const removeSubtopic = (wi, si) => setForm(f => {
    const c = [...f.curriculum];
    c[wi] = { ...c[wi], subtopics: c[wi].subtopics.filter((_,idx)=>idx!==si) };
    return { ...f, curriculum: c };
  });

  /* project helpers */
  const addProject = () => setForm(f => ({ ...f, projects: [...f.projects, { title:'', description:'' }] }));
  const updateProject = (i, k, v) => setForm(f => {
    const p = [...f.projects]; p[i] = { ...p[i], [k]: v }; return { ...f, projects: p };
  });
  const removeProject = (i) => setForm(f => ({ ...f, projects: f.projects.filter((_,idx)=>idx!==i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.slug.trim())  { toast.error('Slug is required'); return; }
    setSaving(true);
    try {
      if (editItem) { await updateCourse(editItem._id, form); toast.success('Course updated! ✅'); }
      else          { await createCourse(form);               toast.success('Course created! ✅'); }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggle = async (course) => {
    try {
      await updateCourse(course._id, { ...course, isActive: !course.isActive });
      toast.success(course.isActive ? 'Course hidden' : 'Course visible ✅'); load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (course) => {
    if (!window.confirm('Delete ' + course.title + '? This cannot be undone.')) return;
    try { await deleteCourse(course._id); toast.success('Course deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const SectionBox = ({ title, children }) => (
    <div style={{ background:'var(--bg)',borderRadius:10,padding:16,marginBottom:16 }}>
      <div style={{ fontWeight:700,marginBottom:14,fontSize:14 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div style={{ padding:24 }}>
      <style>{`
        .modal-tabs { display:flex; gap:3; background:#f1f5f9; borderRadius:8px; padding:4px; marginBottom:20px; }
        .modal-tab  { flex:1; padding:8px; border:none; borderRadius:6px; fontSize:13px; fontWeight:600; cursor:pointer; fontFamily:var(--font-display); transition:0.2s; }
        .modal-tab.on  { background:white; color:var(--primary); boxShadow:0 1px 3px rgba(0,0,0,0.1); }
        .modal-tab:not(.on) { background:transparent; color:var(--mid); }
        .subtopic-chip { background:#e2e8f0; color:var(--dark-3); fontSize:12px; padding:3px 9px; borderRadius:999px; display:inline-flex; alignItems:center; gap:5px; marginRight:4px; marginBottom:4px; }
      `}</style>

      {/* ── MODAL ── */}
      {modal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:700,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto' }}>
          <div style={{ background:'white',borderRadius:16,padding:32,maxWidth:700,width:'100%',maxHeight:'92vh',overflowY:'auto' }}>

            {/* Header */}
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
              <h3 style={{ margin:0 }}>{editItem ? '✏️ Edit Course' : '➕ Add New Course'}</h3>
              <button onClick={() => setModal(false)} style={{ background:'none',border:'none',fontSize:22,cursor:'pointer' }}><FiX /></button>
            </div>

            {/* Tab bar */}
            <div className="modal-tabs">
              {[['basic','📋 Basic Info'],['content','📚 Content & Curriculum'],['internship','🎯 Internship & Placement']].map(([id,label]) => (
                <button key={id} className={`modal-tab${tab===id?' on':''}`} onClick={() => setTab(id)}>{label}</button>
              ))}
            </div>

            {/* ── TAB 1: Basic Info ── */}
            {tab === 'basic' && (
              <div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Course Title *</label>
                    <input value={form.title} onChange={e => setF('title', e.target.value)} className="form-control" placeholder="Full Stack Web Development" />
                  </div>
                  <div className="form-group">
                    <label>Slug * <span style={{ fontSize:11,color:'var(--mid)',fontWeight:400 }}>(URL: /courses/your-slug)</span></label>
                    <input value={form.slug} onChange={e => setF('slug', e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,''))} className="form-control" placeholder="full-stack-web-development" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Subtitle <span style={{ fontSize:11,color:'var(--mid)',fontWeight:400 }}>(shown under title on course page)</span></label>
                  <input value={form.subtitle} onChange={e => setF('subtitle', e.target.value)} className="form-control" placeholder="Become a job-ready Full Stack Developer in 3 months" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={form.description} onChange={e => setF('description', e.target.value)} className="form-control" rows={3} style={{ resize:'vertical' }} placeholder="What students will learn in this course..." />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Duration</label>
                    <input value={form.duration} onChange={e => setF('duration', e.target.value)} className="form-control" placeholder="3 Months" />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select value={form.category} onChange={e => setF('category', e.target.value)} className="form-control">
                      <option value="Development">Development</option>
                      <option value="Data Science">Data Science</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Design">Design</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Badge</label>
                    <input value={form.badge} onChange={e => setF('badge', e.target.value)} className="form-control" placeholder="Most Popular / New / Hot" />
                  </div>
                  <div className="form-group">
                    <label>Rating (1–5)</label>
                    <input type="number" value={form.rating} onChange={e => setF('rating', parseFloat(e.target.value)||4.8)} className="form-control" min={1} max={5} step={0.1} />
                  </div>
                </div>

                <SectionBox title="💰 Fee Structure">
                  <div className="form-grid">
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Total Fee (₹)</label>
                      <input type="number" value={form.fee.total} onChange={e => setFee('total', e.target.value)} className="form-control" />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Registration Fee (₹)</label>
                      <input type="number" value={form.fee.registration} onChange={e => setFee('registration', e.target.value)} className="form-control" />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Remaining Fee (₹)</label>
                      <input type="number" value={form.fee.remaining} onChange={e => setFee('remaining', e.target.value)} className="form-control" />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Status</label>
                      <select value={form.isActive?'true':'false'} onChange={e => setF('isActive', e.target.value==='true')} className="form-control">
                        <option value="true">Active (visible)</option>
                        <option value="false">Inactive (hidden)</option>
                      </select>
                    </div>
                  </div>
                </SectionBox>
              </div>
            )}

            {/* ── TAB 2: Content & Curriculum ── */}
            {tab === 'content' && (
              <div>
                {/* Highlights */}
                <SectionBox title="✅ Course Highlights">
                  <p style={{ fontSize:13,color:'var(--mid)',marginBottom:12 }}>
                    These are the bullet points shown on the course card. E.g. "Build 5+ real-world projects", "1-month paid internship"
                  </p>
                  <StringList
                    label="Add Highlight"
                    values={form.highlights}
                    onChange={v => setF('highlights', v)}
                    placeholder="e.g. Build 5+ real-world projects"
                  />
                </SectionBox>

                {/* Tools */}
                <SectionBox title="🛠️ Tools & Technologies">
                  <p style={{ fontSize:13,color:'var(--mid)',marginBottom:12 }}>
                    Technologies students will learn. E.g. React, Node.js, MongoDB, Python
                  </p>
                  <StringList
                    label="Add Tool"
                    values={form.tools}
                    onChange={v => setF('tools', v)}
                    placeholder="e.g. React.js"
                  />
                </SectionBox>

                {/* Projects */}
                <SectionBox title="💻 Projects">
                  <p style={{ fontSize:13,color:'var(--mid)',marginBottom:12 }}>
                    Real-world projects students will build during the course.
                  </p>
                  {form.projects.map((p, i) => (
                    <div key={i} style={{ border:'1px solid var(--border)',borderRadius:8,padding:14,marginBottom:10,background:'white' }}>
                      <div style={{ display:'flex',gap:8,marginBottom:8 }}>
                        <input value={p.title} onChange={e => updateProject(i,'title',e.target.value)}
                          className="form-control" placeholder="Project title e.g. E-commerce Website" style={{ flex:1 }} />
                        <button onClick={() => removeProject(i)} className="btn btn-sm" style={{ background:'#fee2e2',color:'var(--error)',flexShrink:0 }}><FiTrash2 size={13}/></button>
                      </div>
                      <textarea value={p.description} onChange={e => updateProject(i,'description',e.target.value)}
                        className="form-control" rows={2} placeholder="Brief description of this project..." style={{ resize:'vertical' }} />
                    </div>
                  ))}
                  <button onClick={addProject} className="btn btn-outline btn-sm"><FiPlus size={13}/> Add Project</button>
                </SectionBox>

                {/* Curriculum */}
                <SectionBox title="📅 Weekly Curriculum">
                  <p style={{ fontSize:13,color:'var(--mid)',marginBottom:12 }}>
                    Week-by-week breakdown of what will be taught.
                  </p>
                  {form.curriculum.map((w, wi) => (
                    <div key={wi} style={{ border:'1px solid var(--border)',borderRadius:8,padding:14,marginBottom:10,background:'white' }}>
                      <div style={{ display:'flex',gap:8,marginBottom:8 }}>
                        <input value={w.week} onChange={e => updateCurriculumWeek(wi,'week',e.target.value)}
                          className="form-control" placeholder="Week 1" style={{ width:100,flexShrink:0 }} />
                        <input value={w.topic} onChange={e => updateCurriculumWeek(wi,'topic',e.target.value)}
                          className="form-control" placeholder="Topic e.g. HTML & CSS Basics" style={{ flex:1 }} />
                        <button onClick={() => removeCurriculumWeek(wi)} className="btn btn-sm" style={{ background:'#fee2e2',color:'var(--error)',flexShrink:0 }}><FiTrash2 size={13}/></button>
                      </div>
                      {/* Subtopics */}
                      <div style={{ paddingLeft:8 }}>
                        <div style={{ fontSize:12,color:'var(--mid)',marginBottom:6 }}>Subtopics:</div>
                        <div style={{ marginBottom:8 }}>
                          {(w.subtopics||[]).map((s, si) => (
                            <span key={si} className="subtopic-chip">
                              {s}
                              <button onClick={() => removeSubtopic(wi,si)} style={{ background:'none',border:'none',cursor:'pointer',color:'#64748b',lineHeight:1,padding:0,fontSize:14 }}>×</button>
                            </span>
                          ))}
                        </div>
                        <SubtopicInput onAdd={val => addSubtopic(wi, val)} />
                      </div>
                    </div>
                  ))}
                  <button onClick={addCurriculumWeek} className="btn btn-outline btn-sm"><FiPlus size={13}/> Add Week</button>
                </SectionBox>
              </div>
            )}

            {/* ── TAB 3: Internship & Placement ── */}
            {tab === 'internship' && (
              <div>
                <SectionBox title="🎯 Internship Details">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Duration</label>
                      <input value={form.internshipDetails.duration} onChange={e => setInternship('duration',e.target.value)} className="form-control" placeholder="1 Month" />
                    </div>
                    <div className="form-group">
                      <label>Type</label>
                      <input value={form.internshipDetails.type} onChange={e => setInternship('type',e.target.value)} className="form-control" placeholder="Remote / Hybrid / On-site" />
                    </div>
                    <div className="form-group">
                      <label>Stipend</label>
                      <input value={form.internshipDetails.stipend} onChange={e => setInternship('stipend',e.target.value)} className="form-control" placeholder="Performance Based / ₹5000/month" />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label>Description</label>
                    <textarea value={form.internshipDetails.description} onChange={e => setInternship('description',e.target.value)}
                      className="form-control" rows={3} placeholder="After completing the course, students get a 1-month paid internship with our partner companies..." />
                  </div>
                </SectionBox>

                <SectionBox title="🏆 Placement Assistance">
                  <p style={{ fontSize:13,color:'var(--mid)',marginBottom:14 }}>All these are automatically enabled for all courses:</p>
                  {[
                    ['Resume Building', '📄'],
                    ['Mock Interviews', '🎤'],
                    ['Job Referrals', '🤝'],
                    ['LinkedIn Optimization', '💼'],
                  ].map(([label, icon]) => (
                    <div key={label} style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'white',border:'1px solid var(--border)',borderRadius:8,marginBottom:8 }}>
                      <span style={{ fontSize:18 }}>{icon}</span>
                      <span style={{ fontWeight:500,fontSize:14 }}>{label}</span>
                      <span style={{ marginLeft:'auto',background:'#d1fae5',color:'var(--success)',fontSize:12,fontWeight:700,padding:'2px 10px',borderRadius:999 }}>✓ Included</span>
                    </div>
                  ))}
                </SectionBox>
              </div>
            )}

            {/* Actions */}
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:20,paddingTop:16,borderTop:'1px solid var(--border)' }}>
              <div style={{ display:'flex',gap:8 }}>
                {tab !== 'basic' && <button onClick={() => setTab(tab==='internship'?'content':'basic')} className="btn btn-outline btn-sm">← Back</button>}
                {tab !== 'internship' && <button onClick={() => setTab(tab==='basic'?'content':'internship')} className="btn btn-outline btn-sm">Next →</button>}
              </div>
              <div style={{ display:'flex',gap:10 }}>
                <button onClick={() => setModal(false)} className="btn btn-outline">Cancel</button>
                <button onClick={save} disabled={saving} className="btn btn-primary">
                  {saving ? 'Saving...' : editItem ? '✓ Update Course' : '✓ Create Course'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Course list ── */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
        <h3 style={{ margin:0 }}>Courses ({courses.length})</h3>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={load} className="btn btn-outline btn-sm"><FiRefreshCw size={13}/> Refresh</button>
          <button onClick={openAdd} className="btn btn-primary btn-sm"><FiPlus size={13}/> Add Course</button>
        </div>
      </div>

      {loading ? <div className="spinner-wrap"><div className="spinner"/></div> : (
        <div style={{ display:'grid',gap:14 }}>
          {courses.map(c => (
            <div key={c._id} style={{ background:'white',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'18px 22px',display:'flex',alignItems:'center',gap:18,opacity:c.isActive?1:0.65 }}>
              <div style={{ width:50,height:50,borderRadius:12,background:'var(--primary-light)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,flexShrink:0 }}>
                {c.category==='Development'?'💻':c.category==='Data Science'?'🤖':c.category==='Design'?'🎨':'📱'}
              </div>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontWeight:700,fontSize:15,marginBottom:3 }}>
                  {c.title}
                  {c.badge && <span style={{ marginLeft:8,background:'var(--accent-light)',color:'var(--accent)',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:999 }}>{c.badge}</span>}
                  {!c.isActive && <span style={{ marginLeft:8,background:'#f1f5f9',color:'var(--mid)',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:999 }}>Hidden</span>}
                </div>
                <div style={{ fontSize:12,color:'var(--mid)' }}>
                  {c.duration} · {c.category} · {c.enrolledCount||0} enrolled · ⭐ {c.rating}
                  {c.highlights?.length > 0 && <span style={{ marginLeft:8,color:'var(--success)' }}>✓ {c.highlights.length} highlights</span>}
                  {c.curriculum?.length > 0 && <span style={{ marginLeft:8,color:'var(--primary)' }}>📅 {c.curriculum.length} weeks</span>}
                  {c.projects?.length > 0 && <span style={{ marginLeft:8,color:'var(--accent)' }}>💻 {c.projects.length} projects</span>}
                </div>
              </div>
              <div style={{ textAlign:'right',flexShrink:0 }}>
                <div style={{ fontWeight:800,fontSize:18,fontFamily:'var(--font-display)',color:'var(--success)' }}>₹{(c.fee?.total||0).toLocaleString()}</div>
                <div style={{ fontSize:11,color:'var(--mid)' }}>Reg: ₹{(c.fee?.registration||0).toLocaleString()}</div>
              </div>
              <div style={{ display:'flex',gap:8,flexShrink:0,flexWrap:'wrap' }}>
                <Link to={`/courses/${c.slug}`} target="_blank" className="btn btn-outline btn-sm">View</Link>
                <button onClick={() => toggle(c)} className="btn btn-outline btn-sm">{c.isActive?'Hide':'Show'}</button>
                <button onClick={() => openEdit(c)} className="btn btn-primary btn-sm"><FiEdit2 size={13}/> Edit</button>
                <button onClick={() => remove(c)} className="btn btn-sm" style={{ background:'#fee2e2',color:'var(--error)' }}><FiTrash2 size={13}/></button>
              </div>
            </div>
          ))}
          {courses.length === 0 && (
            <div style={{ textAlign:'center',padding:40,color:'var(--mid)',background:'white',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)' }}>
              No courses yet. Click "Add Course" to create one.
            </div>
          )}
        </div>
      )}
    </div>
);
}

/* SubtopicInput helper */
const SubtopicInput = ({ onAdd }) => {
  const [val, setVal] = React.useState('');
  return (
    <div style={{ display:'flex',gap:6 }}>
      <input value={val} onChange={e => setVal(e.target.value)}
        className="form-control" placeholder="Add subtopic e.g. CSS Flexbox" style={{ fontSize:12,padding:'5px 10px' }}
        onKeyDown={e => { if(e.key==='Enter'){ e.preventDefault(); onAdd(val); setVal(''); } }} />
      <button type="button" onClick={() => { onAdd(val); setVal(''); }}
        className="btn btn-sm" style={{ background:'var(--primary-light)',color:'var(--primary)',flexShrink:0,padding:'4px 10px' }}>+</button>
    </div>
  );
};

/* ─── Testimonials ─── */
const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const blank = { name: '', role: '', course: '', content: '', rating: 5, isActive: true, isFeatured: false };
  const [form, setForm] = useState(blank);

  const load = () => { setLoading(true); getAdminTestimonials().then(r => setTestimonials(r.data.testimonials || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openEdit = (t) => { setEditItem(t); setForm(t); setModal(true); };
  const openNew = () => { setEditItem(null); setForm(blank); setModal(true); };

  const handleSave = async () => {
    try {
      if (editItem) { await updateTestimonial(editItem._id, form); toast.success('Updated!'); }
      else { await createTestimonial(form); toast.success('Created!'); }
      setModal(false); load();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try { await deleteTestimonial(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button className="btn btn-primary" onClick={openNew}><FiPlus size={15} /> Add Testimonial</button>
      </div>
      {loading ? <div className="spinner-wrap"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gap: 14 }}>
          {testimonials.map((t, i) => (
            <div key={t._id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>{t.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{t.name} <span style={{ fontWeight: 400, color: 'var(--mid)', fontSize: 13 }}>— {t.role}</span></div>
                <div style={{ fontSize: 12, color: 'var(--primary)', marginBottom: 6 }}>{t.course}</div>
                <div style={{ fontSize: 13, color: 'var(--dark-3)', lineHeight: 1.6 }}>{t.content}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {t.isFeatured && <span className="badge badge-accent">Featured</span>}
                  <StatusBadge status={t.isActive ? 'active' : 'rejected'} />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(t)} className="btn btn-sm btn-outline"><FiEdit2 size={13} /></button>
                  <button onClick={() => handleDelete(t._id)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}><FiTrash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 'var(--radius-xl)', padding: '32px', maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3>{editItem ? 'Edit' : 'Add'} Testimonial</h3>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}><FiX /></button>
            </div>
            <div className="form-grid">
              {[['name', 'Name'], ['role', 'Role (e.g. Developer at TCS)']].map(([k, l]) => (
                <div key={k} className="form-group">
                  <label>{l}</label>
                  <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="form-control" />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Course</label>
              <select value={form.course} onChange={e => setForm(f => ({ ...f, course: e.target.value }))} className="form-control">
                <option value="">Select Course</option>
                {['Full Stack Web Development', 'Data Science & AI', 'Digital Marketing'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Testimonial Content</label>
              <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} className="form-control" rows={4} />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Rating (1–5)</label>
                <select value={form.rating} onChange={e => setForm(f => ({ ...f, rating: Number(e.target.value) }))} className="form-control">
                  {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Options</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  {[['isActive', 'Active'], ['isFeatured', 'Featured']].map(([k, l]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                      <input type="checkbox" checked={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setModal(false)} className="btn btn-outline">Cancel</button>
              <button onClick={handleSave} className="btn btn-primary">Save Testimonial</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Admin Layout ─── */
const pageTitles = {
  '/admin': 'Dashboard Overview',
  '/admin/applications': 'Applications',
  '/admin/payments': 'Payments',
  '/admin/users': 'Students',
  '/admin/courses': 'Courses',
  '/admin/testimonials': 'Testimonials',
  '/admin/lms':  'LMS Content Manager',
  '/admin/live': 'Live Classes',
};

export default function AdminPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const path = window.location.pathname;
  const title = pageTitles[path] || 'Admin Panel';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <style>{`
        .admin-content { margin-left: 240px; flex: 1; min-height: 100vh; display: flex; flex-direction: column; }
        @media (max-width: 900px) { .admin-content { margin-left: 0; } }
      `}</style>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199 }} onClick={() => setSidebarOpen(false)} />}
      <div className="admin-content">
        <TopBar title={title} onMenuClick={() => setSidebarOpen(s => !s)} />
        <div style={{ flex: 1 }}>
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="users" element={<AdminStudents />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="lms" element={<AdminLmsPage />} />
            <Route path="live" element={<AdminLiveClasses />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}